/**
 * CircleObject - Base class for all circular game entities
 * by littlefean
 */
import { Vector } from '../../core/math/vector';
import { Circle } from '../../core/math/circle';
import { MyColor, ReadonlyColor } from '../myColor';
import {
    renderStatusBar,
    createStatusBarCache,
    BAR_OFFSET,
    type StatusBarCache
} from '../statusBar';
import type { CheatModeLike } from '@/types/worldLike';

export interface CircleObjectWorldLike {
    width: number;
    height: number;
    cheatMode?: CheatModeLike;
    markSpatialDirty?(entity: unknown): void;
}

export class CircleObject {
    // Cached font string to avoid repeated string creation
    static FONT_9 = "9px Microsoft YaHei";
    // Static temp vector for move() to avoid GC pressure
    private static _tempVec: Vector = new Vector(0, 0);

    pos: Vector;
    world: CircleObjectWorldLike;
    gameType: string;
    liveTime: number;
    r: number;

    speed: Vector;
    acceleration: Vector;
    accelerationV: number;
    maxSpeedN: number;

    hp: number;
    maxHp: number;

    hpColor: MyColor;
    bodyColor: MyColor;
    bodyStrokeWidth: number;
    bodyStrokeColor: ReadonlyColor;
    hpBarHeight: number;

    comment: string;
    selected: boolean;

    protected _bodyCircle: Circle | null;
    _hpBarCache: StatusBarCache;

    _bodyVersion: number = 0;

    // Territory related properties (public for Territory system access)
    inValidTerritory: boolean;
    _originalMaxHp: number | null;
    _territoryPenaltyApplied: boolean;
    _originalRangeR: number | null;

    // 上一帧位置（用于扫掠碰撞检测）
    prevX: number = 0;
    prevY: number = 0;

    // Owner ID for multiplayer support (null = neutral/single-player default)
    ownerId: string | null = null;

    // Unique ID for multiplayer entity tracking (null = single-player)
    id: string | null = null;

    constructor(pos: Vector, world: CircleObjectWorldLike) {
        this.pos = pos;
        this.world = world;
        this.gameType = "CircleObject";
        this.liveTime = 0;
        this.r = 20;

        this.speed = new Vector(0, 0);
        this.acceleration = new Vector(0, 0);
        this.accelerationV = 0;
        this.maxSpeedN = 100;

        this.hp = 100;
        this.maxHp = 100;

        this.hpColor = new MyColor(122, 12, 12, 1);
        this.bodyColor = new MyColor(20, 20, 20, 1);
        this.bodyStrokeWidth = 5;
        this.bodyStrokeColor = MyColor.Transparent();
        this.hpBarHeight = 10;

        this.comment = "细节待补充.....内容还在完善中。。。";
        this.selected = false;

        this._bodyCircle = null;
        this._hpBarCache = createStatusBarCache();

        this.inValidTerritory = true;
        this._originalMaxHp = null;
        this._territoryPenaltyApplied = false;
        this._originalRangeR = null;
        this._bodyVersion = 0;

        // 初始化上一帧位置
        this.prevX = pos.x;
        this.prevY = pos.y;
    }

    /**
     * Initialize entity HP and fill to max
     */
    hpInit(n: number): void {
        this.hp = n;
        this.maxHp = n;
        this._markBodyDirty();
    }

    protected _markBodyDirty(): void {
        this._bodyVersion++;
    }

    protected _markMovement(prevX: number, prevY: number): void {
        if (prevX !== this.pos.x || prevY !== this.pos.y) {
            this.world.markSpatialDirty?.(this);
        }
    }

    /**
     * Change entity HP
     */
    hpChange(dh: number): void {
        // Infinite HP cheat for towers and buildings
        if (dh < 0 && this.world && this.world.cheatMode) {
            if (this.world.cheatMode.enabled && this.world.cheatMode.infiniteHp) {
                if (this.gameType === "Tower" || this.gameType === "Building" || this.gameType === "Mine") {
                    return;
                }
            }
        }

        if (this.hp + dh <= 0) {
            this.hp = 0;
            this._markBodyDirty();
            return;
        } else if (this.hp + dh >= this.maxHp) {
            this.hp = this.maxHp;
            this._markBodyDirty();
            return;
        }
        this.hp += dh;
        this._markBodyDirty();
    }

    hpSet(hp: number): void {
        // Infinite HP cheat for towers and buildings
        if (hp < this.hp && this.world && this.world.cheatMode) {
            if (this.world.cheatMode.enabled && this.world.cheatMode.infiniteHp) {
                if (this.gameType === "Tower" || this.gameType === "Building" || this.gameType === "Mine") {
                    return;
                }
            }
        }

        if (hp > this.maxHp) {
            this.hp = this.maxHp;
            this._markBodyDirty();
            return;
        }
        if (hp < 0) {
            this.hp = 0;
            this._markBodyDirty();
            return;
        }
        this.hp = hp;
        this._markBodyDirty();
    }

    /**
     * Change body radius
     */
    bodyRadiusChange(dr: number): void {
        if (this.r + dr <= 0) {
            this.r = 0;
            this.remove();
        } else {
            this.r += dr;
        }
        this.world.markSpatialDirty?.(this);
        this._markBodyDirty();
    }

    /**
     * Check if dead
     */
    isDead(): boolean {
        if (this.maxHp < 0) {
            return true;
        }
        return this.hp <= 0;
    }

    goStep(): void {
        this.liveTime++;
    }

    /**
     * Remove entity
     */
    remove(): void {
        this.pos.x = -10000;
        this.pos.y = -10000;
    }

    getBodyCircle(): Circle {
        if (!this._bodyCircle) {
            this._bodyCircle = new Circle(this.pos.x, this.pos.y, this.r);
        }
        let res = this._bodyCircle;
        res.x = this.pos.x;
        res.y = this.pos.y;
        res.r = this.r;
        res.pos.x = this.pos.x;
        res.pos.y = this.pos.y;
        res.setFillColor(this.bodyColor);
        res.setStrokeColor(this.bodyStrokeColor);
        let hpRate = this.hp / this.maxHp;
        res.setStrokeWidth(this.bodyStrokeWidth * hpRate);
        return res;
    }

    move(): void {
        // 保存上一帧位置（用于扫掠碰撞检测）
        this.prevX = this.pos.x;
        this.prevY = this.pos.y;

        if (this.acceleration.x === this.acceleration.y && this.acceleration.x === 0) {
            // Use static temp vector to avoid GC pressure
            Vector.mulTo(this.speed, this.accelerationV, CircleObject._tempVec);
            this.speed.add(CircleObject._tempVec);
        } else {
            this.speed.add(this.acceleration);
        }
        const maxSpeedSq = this.maxSpeedN * this.maxSpeedN;
        if (this.speed.absSq() > maxSpeedSq) {
            // Use in-place operations to avoid GC pressure
            this.speed.mulInPlace(this.maxSpeedN / this.speed.abs());
        }
        this.pos.add(this.speed);
        this._markMovement(this.prevX, this.prevY);
    }

    isInScreen(): boolean {
        let xIn = -this.r < this.pos.x && this.pos.x < this.world.width + this.r;
        let yIn = -this.r < this.pos.y && this.pos.y < this.world.height + this.r;
        return xIn && yIn;
    }

    /**
     * Check if far outside screen
     */
    isOutScreen(): boolean {
        let margin = 500;
        let xIn = -this.r - margin < this.pos.x && this.pos.x < this.world.width + this.r + margin;
        let yIn = -this.r - margin < this.pos.y && this.pos.y < this.world.height + this.r + margin;
        return !(xIn && yIn);
    }

    /**
     * Render
     */

    /**
     * 渲染实体主体（不含状态条）
     * 子类可覆盖此方法自定义主体渲染
     */
    renderBody(ctx: CanvasRenderingContext2D): void {
        if (!this.isInScreen()) {
            return;
        }

        const c = this.getBodyCircle();
        c.render(ctx);
    }

    render(ctx: CanvasRenderingContext2D): void {
        this.renderBody(ctx);
        this.renderBars(ctx);
    }

    /**
     * 渲染状态条（血条等）
     * 子类可覆盖此方法添加额外的状态条
     */
    renderBars(ctx: CanvasRenderingContext2D): void {
        if (!this.isInScreen()) {
            return;
        }
        if (this.maxHp > 0 && !this.isDead()) {
            const barH = this.hpBarHeight;
            const barX = this.pos.x - this.r;
            const barY = this.pos.y - this.r + BAR_OFFSET.HP_TOP * barH;
            const barW = this.r * 2;
            const hpRate = this.hp / this.maxHp;

            renderStatusBar(ctx, {
                x: barX,
                y: barY,
                width: barW,
                height: barH,
                fillRate: hpRate,
                fillColor: this.hpColor,
                showText: true,
                textValue: this.hp,
                cache: this._hpBarCache
            });
        }
    }
}
